import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import styled from 'styled-components';
import Swal from 'sweetalert2';
import * as Sentry from '@sentry/react';
import { setAdditionalData } from '../../../reducers/additionalDataSlice';
import {
  addIncentiveToOrder,
  setOptionalIncentiveToConfigure,
} from '../../../actions/v3Order';
import { loadShootContextForOrder } from '../../../actions/shootContext';
import { canReloadShootForOrder } from '../dashboard/galleryOrderContext';
import cartUtils from '../../../utils/cart';
import {
  selectAvailableOptionalBonuses,
  selectOrderKeysInActiveShoot,
  selectUnresolvedOptionalCartLevelIncentives,
} from '../../../selectors';

const Wrapper = styled.li`
  display: block;
  border-radius: 4px;
  border: 1px solid #ccc;
  background-color: #fff;
  box-shadow: 0px 4px 6px rgba(0, 0, 0, 0.1);
  padding: 24px;
  margin-bottom: 36px;
  @media (max-width: 767px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #479de6;
  margin-bottom: 12px;
`;

const BonusList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const BonusItem = styled.li`
  align-items: center;
  border-top: ${(props) => (props.isFirst ? 'none' : '1px solid #ddd')};
  display: flex;
  gap: 16px;
  padding: 16px 0;
`;

const ProductImage = styled.img`
  border: 1px solid #ddd;
  max-width: 72px;
  width: 72px;
`;

const ProductName = styled.div`
  font-weight: 600;
`;

const ProductMeta = styled.div`
  color: #555;
  font-size: 13px;
  margin-top: 4px;
`;

const LoadingLabel = styled.div`
  color: #555;
  font-weight: 600;
  margin-left: auto;
  padding: 8px 0;
`;

const AddButton = styled.button`
  background: transparent;
  border: none;
  color: #479de6;
  font-weight: 600;
  margin-left: auto;
  padding: 8px 0;
  &:hover {
    color: #479de6;
    text-decoration: underline;
  }
`;

// The cart's cross-order surface for declined-but-still-earned bonuses. The
// list is a pure function of cart contents (ENG-2043 R4) — every entry is
// already scoped to an opt-in order by selectAvailableOptionalBonuses, so
// nothing here reads the active shoot's opt-in flag or catalog.
const AvailableBonuses = () => {
  const dispatch = useDispatch();
  const reportedUnresolvedIncentives = useRef(new Set());
  const availableBonuses = useSelector(selectAvailableOptionalBonuses);
  const unresolvedCartLevelIncentives = useSelector(
    selectUnresolvedOptionalCartLevelIncentives,
  );
  // Which orders can be configured right now without loading their shoot.
  const orderKeysInActiveShoot = useSelector(selectOrderKeysInActiveShoot);
  // The row whose shoot is loading, so only that row swaps its button for a
  // status while the parent waits (ENG-2043 re-add Tier 3).
  const [loadingBonusKey, setLoadingBonusKey] = useState(null);

  useEffect(() => {
    unresolvedCartLevelIncentives.forEach(({ product, reason }) => {
      const extra = {
        reason,
        incentiveProductId: product.id,
        companyPricingModelId: product.company_pricing_model_id ?? null,
      };
      const diagnosticKey = JSON.stringify(extra);

      if (reportedUnresolvedIncentives.current.has(diagnosticKey)) {
        return;
      }

      reportedUnresolvedIncentives.current.add(diagnosticKey);
      Sentry.captureMessage(
        'Unable to resolve cart-level optional incentive owner',
        {
          level: 'error',
          extra,
        },
      );
    });
  }, [unresolvedCartLevelIncentives]);

  if (availableBonuses.length === 0) {
    return null;
  }

  const bonusKey = ({ scope, orderKey, product }) =>
    `available-bonus-${scope}-${orderKey}-${product.id}`;

  const studentName = (order) =>
    [order?.student?.firstName, order?.student?.lastName]
      .filter(Boolean)
      .join(' ');

  // Hand the bonus to the product configurator in its OWN order's context:
  // the student it belongs to, and a marker naming the order, the product and
  // (for a cart-level benefit) the sku, so the add lands back in that order
  // however the configurator resolves the row.
  const configureBonus = ({ order, orderKey, product }) => {
    dispatch(
      setAdditionalData({
        ...(order.additionalData || {}),
        selectedStudent: order.student,
        studentId: order.student?.id,
      }),
    );
    dispatch(
      setOptionalIncentiveToConfigure({
        orderKey,
        productId: product.id,
        sku: product.sku ?? null,
      }),
    );
    dispatch(push('/v3/incentives/config'));
  };

  const sendThroughDashboard = (order) => {
    const name = studentName(order);
    const eventName = order?.event?.name || 'gallery';
    Swal.fire({
      title: 'Open this gallery to add your bonus',
      text: name
        ? `To add this bonus, open ${name}'s ${eventName} gallery from your dashboard.`
        : `To add this bonus, open the ${eventName} gallery from your dashboard.`,
      icon: 'info',
    });
    dispatch(push('/dashboard'));
  };

  // Four ways back into the cart, in order of how little they disturb the
  // parent (ENG-2043 "Re-add flow"). Every one of them puts the item in the
  // order that EARNED it — never in whichever order the active shoot implies.
  const addBonus = async (bonus) => {
    const { order, orderKey, product } = bonus;

    // Tier 1 — nothing to choose (free shipping, and any bonus with no pose,
    // background, required field or personalization step): straight into the
    // order, no configurator, no navigation, no dependence on state.shoot.
    if (!cartUtils.productRequiresIncentiveConfiguration(product)) {
      dispatch(addIncentiveToOrder({ orderKey, product }));
      return;
    }

    // The configurator adds through the active student, so a configurable
    // bonus on an order that lost its student record can only be re-added by
    // reopening that gallery (which restores the student) — never by pushing
    // a null selectedStudent into the add-to-cart path.
    if (!order?.student?.id) {
      sendThroughDashboard(order);
      return;
    }

    // Tier 2 — configurable, and this order's shoot is already the loaded
    // one: today's flow, which needs nothing loaded first.
    if (orderKeysInActiveShoot.includes(orderKey)) {
      configureBonus(bonus);
      return;
    }

    // Tier 4 — configurable, another shoot, and the order predates
    // `shootLoadParams` (or was placed without a gallery key): we cannot
    // reload its context, so send the parent through the dashboard. The
    // decline stays listed, and the bonus becomes a Tier 2 add afterwards.
    if (!canReloadShootForOrder(order)) {
      sendThroughDashboard(order);
      return;
    }

    // Tier 3 — configurable, another shoot, but the order knows how to reload
    // it. Load it FULLY before routing: the configurator reads backgrounds,
    // poses and price-list flags from state.shoot, and usePreventNavigation
    // blocks route changes once it has mounted.
    setLoadingBonusKey(bonusKey(bonus));

    try {
      await dispatch(loadShootContextForOrder(order));
    } catch {
      Swal.fire({
        title: 'We could not open that gallery',
        text: `We couldn't load ${
          order?.event?.name || 'that school'
        }. Open it from your dashboard to add this bonus.`,
        icon: 'error',
      });
      return;
    } finally {
      setLoadingBonusKey(null);
    }

    configureBonus(bonus);
  };

  // Which order earned the bonus, in the parent's terms: the student and the
  // school. A cart-level benefit belongs to the whole cart but is still
  // earned at (and re-added through) one school's price list.
  const bonusLabel = ({ order, scope }) => {
    const eventName = order?.event?.name;
    const student = order?.student;

    if (scope === 'cart_level' || !student) {
      return eventName
        ? `Qualified for your cart · earned at ${eventName}`
        : 'Qualified for your cart';
    }

    const name = studentName(order);

    return eventName
      ? `Qualified for ${name} · ${eventName}`
      : `Qualified for ${name}`;
  };

  return (
    <Wrapper>
      <Header>Available Bonuses</Header>
      <BonusList>
        {availableBonuses.map((bonus, index) => (
          <BonusItem isFirst={index === 0} key={bonusKey(bonus)}>
            {bonus.product.image_thumbnail && (
              <ProductImage
                alt={bonus.product.name}
                src={bonus.product.image_thumbnail}
              />
            )}
            <div>
              <ProductName>{bonus.product.name}</ProductName>
              <ProductMeta>{bonusLabel(bonus)}</ProductMeta>
            </div>
            {loadingBonusKey === bonusKey(bonus) ? (
              <LoadingLabel role="status">
                {`Loading ${bonus.order?.event?.name || 'gallery'}…`}
              </LoadingLabel>
            ) : (
              <AddButton type="button" onClick={() => addBonus(bonus)}>
                Add Bonus
              </AddButton>
            )}
          </BonusItem>
        ))}
      </BonusList>
    </Wrapper>
  );
};

export default AvailableBonuses;
